import { useDispatch, useSelector } from "react-redux";
import {
  createCodeBlock,
  deleteCodeBlock,
  getCodeBlock,
  runCodeBlock,
  setCodeBlockCode,
} from "../api/codeBlock";
import { useAppId } from "../hook";
import { ActionType, AppDispatch, RootState } from "../reducer";
import { AddCodeBlocksAction } from "../reducer/codeBlock";
import { CodeBlock, CodeBlockNoteBlock } from "../types/codeBlock";
import { NotePageData } from "../types/note";
import { isCodeBlockPageBlock } from "../types/notebook/note/block/codeBlock";
import { SearchTableRequest, Table } from "../types/table";
import { useFetchTables } from "./table";
import {
  useFetchNotebookPage,
  useSelectedNotebookPageId,
} from "./page";
import { useAppIdMap } from "./app";

// export const getCodeBlockIdsFromNote: (note: NotePageData) => string[] = (
//   note
// ) => {
//   return (note?.blocks ?? [])
//     .filter((block) => isCodeBlockPageBlock(block))
//     .map((block) => block as CodeBlockNoteBlock)
//     .map((block) => block["code-block-id"]);
// };

export const useAddCodeBlocks: () => (codeBlocks: CodeBlock[]) => void = () => {
  const dispatch = useDispatch<AppDispatch>();
  return (codeBlocks) => {
    const action: AddCodeBlocksAction = {
      type: ActionType.ADD_CODE_BLOCKS,
      codeBlocks,
    };
    dispatch(action as any);
  };
};

// export const useInsertCodeBlock: () => (config: {
//   blockIndex: number;
// }) => Promise<any> = () => {
//   const [appId] = useAppId();
//   const [selectedPageId] = useSelectedNotebookPageId();
//   const fetchNotebookPage = useFetchNotebookPage(selectedPageId);

//   return async ({ blockIndex }) => {
//     if (!appId || !selectedPageId) {
//       throw new Error("No app or page id.");
//     }

//     const block: CodeBlock = {
//       "file-connections": [],
//       "page-id": selectedPageId,
//       "app-id": appId,
//       code: 'print("Hello World");\n',
//       name: "Code Block",
//       description: "Code Block",
//       type: "omega/query-omega/code-block/CodeBlock",
//       "block-index": blockIndex,
//     };

//     return createCodeBlock(block).then(() => {
//       return fetchNotebookPage();
//     });
//   };
// };

export const useFetchCodeBlocks: () => (
  codeBlockIds: string[]
) => Promise<CodeBlock[]> = () => {
  const [appId] = useAppId();
  const addCodeBlocks = useAddCodeBlocks();
  return async (codeBlockIds) => {
    if (!appId) {
      throw new Error("App Id not defined");
    }
    const codeBlocks = await Promise.all(
      codeBlockIds.map((codeBlockId) => getCodeBlock({ appId, codeBlockId }))
    );
    addCodeBlocks(codeBlocks);
    return codeBlocks;
  };
};

export const useSetCodeBlockCode: () => (config: {
  codeBlockId: string;
  code: string;
}) => Promise<any> = () => {
  const [appId] = useAppId();
  const fetchCodeBlocks = useFetchCodeBlocks();
  return async ({ codeBlockId, code }) => {
    if (!appId) {
      throw new Error("App Id not defined");
    }
    const res = await setCodeBlockCode({ appId, codeBlockId, code });
    const fetchRes = await fetchCodeBlocks([codeBlockId]);
  };
};

// export const useFetchPageCodeBlocks: () => (config: {
//   note: NotePageData;
// }) => Promise<CodeBlock[]> = () => {
//   const fetchCodeBlocks = useFetchCodeBlocks();

//   return ({ note }) => {
//     const codeBlockIds = getCodeBlockIdsFromNote(note);
//     return fetchCodeBlocks(codeBlockIds);
//   };
// };

export const useGetCodeBlock: () => (
  codeBlockId: string
) => CodeBlock | undefined = () => {
  const codeBlockMap = useSelector((state: RootState) => state.ui.codeBlocks);

  return (codeBlockId) => {
    return codeBlockId in codeBlockMap ? codeBlockMap[codeBlockId] : undefined;
  };
};

export const useRunCodeBlock: () => (
  codeBlockId: string
) => Promise<CodeBlock> = () => {
  const [appId] = useAppId();
  const fetchCodeBlocks = useFetchCodeBlocks();

  return async (codeBlockId) => {
    if (!appId) {
      throw new Error("No App ID.");
    }
    const codeBlock = await runCodeBlock({ appId, codeBlockId });
    await fetchCodeBlocks([codeBlockId]);
    return codeBlock;
  };
};

export const useDeleteCodeBlock: () => (
  codeBlockId: string
) => Promise<CodeBlockNoteBlock> = () => {
  const [appId] = useAppId();

  return async (codeBlockId) => {
    if (!appId) {
      throw new Error("No App ID.");
    }
    const codeBlock = await deleteCodeBlock({ appId, codeBlockId });
    return codeBlock;
  };
};

export const useFetchCodeBlockTableInputs: () => (config: {
  codeBlocks: CodeBlock[];
}) => Promise<Table[]> = () => {
  const fetchTables = useFetchTables();
  const { appId } = useAppIdMap();

  return async ({ codeBlocks }) => {
    const fileIds = codeBlocks.flatMap(
      (codeBlock) => codeBlock["file-connections"]
    );
    const requests: SearchTableRequest[] = fileIds.map((fileId) => ({
      "app-id": appId!,
      "file-id": fileId,
    }));
    // console.log("FETCHING_CODE_BLOCK_INPUT_TABLES", tableIds);
    return fetchTables({ requests }).then((res) => {
      // console.log("FETCHED_CODE_BLOCK_INPUT_TABLES", res);
      return res;
    });
  };
};
